import type { DeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import type { LegalRole } from "@/shared/hooks/useUserEstateRole";
import type { EssentialModuleId } from "@/shared/types/estateEssentials";
import type { TemplateContext } from "@/shared/utils/templateReplacement";

import {
  getStateContent,
  getStateName,
} from "@/pages/postloss/estate-essentials/data/stateContent";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { toStateAbbreviation } from "@/shared/utils/addressUtils";
import { formatSSN } from "@/shared/utils/formatters";

const VEHICLE_GUIDE_STATES = new Set([
  "CA",
  "TX",
  "NY",
  "FL",
  "IL",
  "PA",
  "OH",
  "GA",
  "NC",
  "MA",
  "WA",
  "VA",
  "NJ",
  "MI",
  "CO",
  "MD",
  "CT",
]);

const PROVIDER_FIELDS = [
  "electric_provider",
  "water_provider",
  "cable_provider",
  "waste_provider",
  "wireless_provider",
  "gas_provider",
] as const;

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function formatDateDisplay(dateStr: string): string {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }
  return dateStr;
}

export interface RoleContext {
  isNominated: boolean;
  isTrustee: boolean;
  isExecutor: boolean;
  isAdministrator: boolean;
  hasLegalAuthority: boolean;
  legalRole: LegalRole;
}

export interface RequestorInfo {
  firstName: string;
  lastName: string;
}

/**
 * Builds a TemplateContext for an estate essentials module by merging:
 * 1. Deceased user data (auto-populated)
 * 2. Role-based boolean flags for conditional blocks
 * 3. State boolean flags for conditional blocks
 * 4. State-specific content for the module
 * 5. User form answers (highest priority override)
 */
export function buildEssentialsContext(
  moduleId: EssentialModuleId,
  formData: Record<string, string>,
  deceasedData: DeceasedUserData | null,
  roleContext?: RoleContext,
  requestor?: RequestorInfo,
): TemplateContext {
  const rawState = formData.deceased_state || deceasedData?.state || formData.state || "";
  const stateCode = rawState ? toStateAbbreviation(rawState) : "";
  const stateName = stateCode ? getStateName(stateCode) : "";
  const stateSpecific = stateCode ? getStateContent(moduleId, stateCode) : {};

  const context: TemplateContext = {
    // Deceased data (auto-populated)
    deceased_first_name: deceasedData?.firstName ?? "",
    deceased_last_name: deceasedData?.lastName ?? "",
    deceased_full_name: deceasedData?.fullName ?? "",
    date_of_death: deceasedData?.dateOfDeath ?? "",
    date_of_birth: deceasedData?.dateOfBirth ?? "",
    deceased_address: deceasedData?.address ?? "",
    deceased_city: deceasedData?.city ?? "",
    deceased_county: deceasedData?.county ?? "",
    deceased_zip: deceasedData?.zipCode ?? "",

    // State
    state: stateCode,
    state_name: stateName,

    // State boolean flag for conditionals (e.g. {{#if state_CA}})
    ...(stateCode ? { [`state_${stateCode}`]: "true" } : {}),

    // Role boolean flags for conditionals
    ...(roleContext?.isTrustee ? { is_trustee: "true" } : {}),
    ...(roleContext?.isExecutor ? { is_executor: "true" } : {}),
    ...(roleContext?.isAdministrator ? { is_administrator: "true" } : {}),
    ...(roleContext?.isNominated ? { is_nominated: "true" } : {}),
    ...(roleContext?.hasLegalAuthority ? { has_legal_authority: "true" } : {}),
    ...(roleContext?.legalRole && roleContext.legalRole !== "none"
      ? { [`role_${roleContext.legalRole}`]: "true", legal_role: roleContext.legalRole }
      : {}),

    // Grouped role flags for template conditionals (maps to ops team notation)
    ...(roleContext?.isExecutor || roleContext?.isAdministrator ? { legal_role_lot: "true" } : {}),
    ...(roleContext?.isAdministrator ? { legal_role_loa: "true" } : {}),
    ...(roleContext?.isTrustee ? { legal_role_trustee: "true" } : {}),
    ...(formData.legal_role === "sea" ? { legal_role_sea: "true" } : {}),

    // Relationship boolean flags for conditionals (e.g. {{#if is_spouse}})
    ...(["spouse", "former-spouse"].includes(formData.user_relationship ?? "")
      ? { is_spouse: "true" }
      : {}),
    ...(formData.user_relationship === "former-spouse" ? { is_former_spouse: "true" } : {}),
    ...(formData.user_relationship === "child" ? { is_child: "true" } : {}),
    ...(formData.user_relationship === "parent" ? { is_parent: "true" } : {}),
    ...(!["spouse", "former-spouse", "child", "parent"].includes(formData.user_relationship ?? "")
      ? { is_other: "true" }
      : {}),

    // Requestor (logged-in user) info
    first_name: requestor?.firstName ?? "",
    last_name: requestor?.lastName ?? "",
    requestor_first_name: requestor?.firstName ?? "",
    requestor_last_name: requestor?.lastName ?? "",

    // State-specific content (resolved per module)
    ...stateSpecific,

    // User form answers (override anything above if keys overlap)
    ...formData,

    // Vehicle state boolean flags (derived from form data after spread)
    ...(formData.vehicle_state ? { [`vehicle_state_${formData.vehicle_state}`]: "true" } : {}),
    ...(formData.vehicle_state && VEHICLE_GUIDE_STATES.has(formData.vehicle_state)
      ? { vehicle_state_has_guide: "true" }
      : {}),

    // Vehicle plate number alias (form stores as license_plate_number)
    vehicle_plate_number: formData.license_plate_number || "",

    // User first name alias for templates
    user_first_name: requestor?.firstName ?? "",

    // Vehicle count flags for conditional blocks
    ...(parseInt(formData.vehicle_count ?? "0", 10) === 1 ? { vehicle_count_single: "true" } : {}),
    ...(parseInt(formData.vehicle_count ?? "0", 10) > 1
      ? { vehicle_count_multiple: "true", vehicle_count: formData.vehicle_count }
      : {}),

    // Utility-specific context
    ...(formData.action_type ? { [`action_${formData.action_type}`]: "true" } : {}),
    // Digital account action flags (memorialize / delete)
    ...(formData.account_action ? { [`action_${formData.account_action}`]: "true" } : {}),
    // Digital account legal role for delete path
    ...(formData.legal_role ? { [`legal_role_${formData.legal_role}`]: "true" } : {}),
    current_utility_type: formData.current_utility_type ?? "electric",
    current_provider: PROVIDER_FIELDS.reduce<string>((found, f) => found || formData[f] || "", ""),
    selected_providers: PROVIDER_FIELDS.reduce<string>(
      (found, f) => found || formData[f] || "",
      "",
    ),
    estate_mailing_address: [
      formData.estate_mailing_address,
      formData.estate_mailing_apartment,
      formData.estate_mailing_city,
      formData.estate_mailing_state,
      formData.estate_mailing_zip,
    ]
      .filter(Boolean)
      .join(", "),

    // Provider boolean flags for conditional provider sections
    ...Object.fromEntries(
      PROVIDER_FIELDS.filter((f) => formData[f]).map((f) => [
        `provider_${toSlug(formData[f])}`,
        "true",
      ]),
    ),

    // Union boolean flag for conditional union sections
    ...(formData.union_name ? { [`union_${toSlug(formData.union_name)}`]: "true" } : {}),
  };

  // Format relationship for display using proper labels
  for (const key of ["user_relationship", "relationship_to_deceased"]) {
    const val = context[key];
    if (val) context[key] = getRelationshipLabel(val as string);
  }

  // Format SSNs for display
  for (const key of ["deceased_ss_number", "user_ss_number"]) {
    if (context[key]) context[key] = formatSSN(context[key] as string);
  }

  // Format dates for display (YYYY-MM-DD → MM/DD/YYYY)
  for (const key of ["date_of_birth", "date_of_death", "user_dob", "marriage_date"]) {
    if (context[key]) context[key] = formatDateDisplay(context[key] as string);
  }

  return context;
}
